import type { TableAppearanceConfig, TableState } from '@konnecta/generic-table';

/**
 * Default number of rows shown per page across all list tables.
 * Change this single value to update the default page size for every list.
 */
export const DEFAULT_LIST_PAGE_SIZE = 25;

/**
 * Shared initial state for all list tables — seeds the default page size.
 * Pass via the `initialState` field of a GenericDataTable `config`.
 */
export const DEFAULT_LIST_INITIAL_STATE: Partial<TableState> = {
  pagination: {
    pageIndex: 0,
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    totalCount: 0,
  },
};

/**
 * Storage version for all list tables. Bump this whenever the persisted table
 * defaults change (e.g. the default page size) so previously stored state in
 * users' localStorage is invalidated and the new defaults take effect.
 */
export const LIST_STORAGE_VERSION = '5';

/**
 * Shared storage config for all list tables.
 * Pass via the `storage` field of a GenericDataTable `config`.
 */
export const DEFAULT_LIST_STORAGE = {
  storageVersion: LIST_STORAGE_VERSION,
} as const;

/**
 * Shared appearance for all list tables built on @konnecta/generic-table.
 *
 * Replaces the previous global CSS overrides — these are passed per-table via
 * the `appearance` prop, so the look is configured through the component API
 * (no Tailwind-scanning or global-stylesheet dependency).
 *
 * Matches the previous antd-based table: compact 12px font, no horizontal cell
 * padding, slate-100 header, and single-line ellipsis truncation.
 */
export const LIST_TABLE_APPEARANCE: TableAppearanceConfig = {
  headerFontSize: '12px',
  cellFontSize: '12px',

  // Header padding — X = left/right, Y = top/bottom (header height)
  headerPaddingX: '10px',
  headerPaddingY: '8px',

  // Data cell padding — X = left/right, Y = top/bottom (row height)
  cellPaddingX: '10px',
  cellPaddingY: '10px',

  headerBackground: '#f1f5f9',
  stickyHeaderBackground: '#f1f5f9',
  sortedHeaderBackground: '#f1f5f9',
  truncateCells: true,
  selectionColumnWidth: 32,
  rowNumberColumnWidth: 40,
};
